<template>
  <span class="d-inline-flex flex-column justify-start">
    <v-progress-circular
      v-if="isLoading"
      color="primary"
      indeterminate
      size="20"></v-progress-circular>
    <template v-else>
      <span class="d-inline-flex flex-column gap-1 justify-start">
        <span class="typography-body-md-bold text--color-text-tertiary" v-if="showName">
          {{ companyName }}
        </span>
        <span v-if="showScac">SCAC: {{ companyScac }}</span>
      </span>
    </template>
  </span>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { get } from 'lodash';

export default {
  name: 'CompanyPresenter',
  props: {
    user: {
      type: Object,
      required: true,
      validator(value) {
        return value?.id || value?.company?.name || value?.company?.scac;
      }
    },
    displayFields: {
      type: Array,
      default: () => ['name'],
      validator(value) {
        return value.every(field => ['name', 'scac'].includes(field));
      }
    }
  },
  setup(props) {
    const isLoading = ref(!props.user?.company?.name);
    const fetchedUser = ref(null);

    const fetchUser = async () => {
      try {
        isLoading.value = true;

        /**
         * TODO: adopt a caching-enabled fetching mechanism
         * like @tanstack/vue-query to avoid the fecthing to happen
         * multiple times?
         */
        const response = await axios.get('carrier', {
          params: {
            s: {
              id: props.user.id
            }
          }
        });

        fetchedUser.value = get(response, 'data.data.0') ?? null;
      } catch (error) {
        // TODO: add a retry mechanism?
        console.error('Failed to get user information:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const displayFields = computed(() => {
      if (Array.isArray(props.displayFields)) {
        return props.displayFields;
      }

      return [];
    });

    const companyName = computed(() => {
      return props.user?.company?.name ?? fetchedUser.value?.company?.name ?? 'Warehouse User';
    });

    const showName = computed(() => {
      return displayFields.value.includes('name');
    });

    const companyScac = computed(() => {
      return props.user?.company?.scac ?? fetchedUser.value?.company?.scac ?? '';
    });

    const showScac = computed(() => {
      return Boolean(displayFields.value.includes('scac') && companyScac.value);
    });

    const fetchUserIfNecessary = () => {
      const mayNeedToFetchName = !props.user?.company?.name && displayFields.value.includes('name');
      const mayNeedToFetchScac = !props.user?.company?.scac && displayFields.value.includes('scac');

      if (
        (mayNeedToFetchName || mayNeedToFetchScac) &&
        props.user?.id &&
        fetchedUser.value?.id !== props.user?.id
      ) {
        fetchUser();
      }
    };

    watch(
      () => props.user,
      () => {
        fetchUserIfNecessary();
      }
    );

    onMounted(() => {
      fetchUserIfNecessary();
    });

    return {
      isLoading,
      companyName,
      companyScac,
      showName,
      showScac
    };
  }
};
</script>

<style scoped></style>
